// src/views/Letters.jsx
// HANDWRITTEN LETTERS — tracks every letter logged with New Call's
// "Confirm Handwritten Letter" (mailing campaign "Handwritten Letter").
// Fetched straight from the database with a contains-query, so it never
// waits on (or loads) the full lead list. Pipeline files that came from a
// lettered lead show the outcome; letters logged on pipeline files directly
// (contactLog type "mail") are included from the in-memory loans.

import React, { useState, useEffect, useMemo } from "react";
import { C, Ic } from '../theme';
import { Btn, Hdr, Inp } from '../ui';
import { loadHandwrittenLetterLeads, fetchLeadById, prospectFromLead, prospectFromLoan, parseDateLocal, convertedLoanFor } from '../lib';

const DAY=864e5;
const PERIODS=[{k:7,l:"7 days"},{k:30,l:"30 days"},{k:90,l:"90 days"},{k:0,l:"All"}];
const LETTER="Handwritten Letter";
// sentAt is the real instant. A bare dateSent ("2026-10-01" / "10/1/2026") is a
// LOCAL day — read at local noon, not UTC midnight (the evening before).
const whenOf=m=>{if(m.sentAt)return m.sentAt;const d=m.dateSent?(parseDateLocal(m.dateSent)||new Date(m.dateSent)):null;return d&&!isNaN(d.getTime())?d.toISOString():"";};
const isLetter=m=>!!m&&m.campaign===LETTER;
// A letter logged on a lead is copied onto the pipeline file it becomes (the
// file's contactLog carries the lead's history), and a letter confirmed on a
// file with a source lead is written to both. Those copies are the SAME
// letter: same mailingId, or (older entries) within a few seconds.
const SAME_LETTER_MS=10000;

function Letters({loans,mktgLeads,adoptLeads,onStartCall}){
  const [leads,setLeads]=useState(null);
  const [err,setErr]=useState("");
  const [period,setPeriod]=useState(30);
  const [q,setQ]=useState("");
  const [opening,setOpening]=useState("");

  useEffect(function(){
    let live=true;
    loadHandwrittenLetterLeads().then(function(r){
      if(!live)return;
      if(r.error){setErr("Couldn't load letters ("+(r.error.message||"unknown")+").");setLeads([]);return;}
      setLeads(r.data);
    });
    return function(){live=false;};
  },[]);

  // One row per letter, newest first.
  const letters=useMemo(function(){
    const out=[];
    // The DB snapshot (fetched once) PLUS any in-memory lead that carries a
    // letter — so a letter logged while this view is open (New Call is an
    // overlay) shows up without leaving and coming back. In-memory copies win
    // (they may have newer edits).
    const byId=new Map();
    (leads||[]).forEach(function(l){byId.set(l.id,l);});
    (mktgLeads||[]).forEach(function(m){if(m&&(byId.has(m.id)||(m.mailings||[]).some(isLetter)))byId.set(m.id,m);});
    const letterIds=new Set(),letterTimes=new Map();
    byId.forEach(function(l){
      const ms=(l.mailings||[]).filter(isLetter);
      const ls=ms.map(function(m){return whenOf(m);}).filter(Boolean).sort();
      ms.forEach(function(m){if(m.id)letterIds.add(m.id);});
      letterTimes.set(l.id,ls.map(function(at){return Date.parse(at);}));
      const file=convertedLoanFor(l,loans);
      ls.forEach(function(at,i){
        const after=l.responded&&l.responded_at&&l.responded_at>at;
        out.push({kind:"lead",person:"lead:"+l.id,key:l.id+"-"+at,at:at,nth:i+1,of:ls.length,lead:l,name:l.name||"(no name)",
          address:[l.address,l.city,l.state].filter(Boolean).join(", ")+(l.zip?" "+l.zip:""),
          phone:l.phone,outcome:file?file.stage:(after?"Responded":(l.doNotContact?"Do not contact":l.status||"")),responded:!!after||!!file});
      });
    });
    (loans||[]).forEach(function(f){
      const near=letterTimes.get(f.sourceLeadId)||[];
      // Skip the file's copy of a letter already listed under its lead.
      const ls=(f.contactLog||[]).filter(function(e){
        if(!e||e.type!=="mail"||!e.at)return false;
        if(e.mailingId)return !letterIds.has(e.mailingId);
        const t=Date.parse(e.at);return !near.some(function(x){return Math.abs(x-t)<=SAME_LETTER_MS;});
      }).map(function(e){return e.at;}).sort();
      ls.forEach(function(at,i){
        out.push({kind:"loan",person:f.sourceLeadId?"lead:"+f.sourceLeadId:"loan:"+f.id,key:f.id+"-"+at,at:at,nth:i+1,of:ls.length,loan:f,name:f.b||"(no name)",address:f.prop||"",phone:f.ph,outcome:f.stage,responded:true});
      });
    });
    return out.sort(function(a,b){return a.at<b.at?1:a.at>b.at?-1:0;});
  },[leads,loans,mktgLeads]);

  const cut=period?new Date(Date.now()-period*DAY).toISOString():"";
  const needle=q.trim().toLowerCase();
  const shown=letters.filter(function(x){
    if(cut&&x.at<cut)return false;
    if(needle&&!(x.name.toLowerCase().includes(needle)||String(x.address).toLowerCase().includes(needle)))return false;
    return true;
  });
  const count=function(days){const c=days?new Date(Date.now()-days*DAY).toISOString():"";return letters.filter(function(x){return !c||x.at>=c;}).length;};
  // One person = one lead, whether their letter shows on the lead or on the
  // pipeline file it became.
  const people=new Set(shown.map(function(x){return x.person;})).size;
  const responded=new Set(shown.filter(function(x){return x.responded;}).map(function(x){return x.person;})).size;

  // The letters query returns a PARTIAL row (no notes, lender, close year…).
  // Opening the console on it and saving wrote those columns back blank, so a
  // lead that isn't in memory is fetched whole first (as Outbound does).
  const work=async function(x){
    if(!onStartCall)return;
    if(x.kind==="loan"){onStartCall({prospect:prospectFromLoan(x.loan)});return;}
    const inMem=(mktgLeads||[]).find(function(m){return m.id===x.lead.id;});
    if(inMem){onStartCall({prospect:prospectFromLead(inMem)});return;}
    setOpening(x.lead.id);setErr("");
    let full=null;
    try{full=await fetchLeadById(x.lead.id);}catch(e){full=null;}
    setOpening("");
    if(!full){setErr("Couldn't load "+(x.lead.name||"that lead")+"'s full record — check your connection and try again.");return;}
    if(adoptLeads)adoptLeads([full]);
    onStartCall({prospect:prospectFromLead(full)});
  };

  const stat=function(v,l,c){return <div style={{flex:"1 1 130px",background:C.card,border:"1px solid "+C.border,borderRadius:12,padding:"12px 16px"}}>
    <div style={{fontSize:22,fontWeight:800,color:c||C.text}}>{v}</div>
    <div style={{fontSize:10.5,fontWeight:800,color:C.textDim,textTransform:"uppercase",letterSpacing:.6}}>{l}</div></div>;};

  return(<div>
    <Hdr t="Handwritten Letters" s="Every letter you've logged with “Confirm Handwritten Letter”, newest first."/>
    {err&&<div style={{padding:"10px 14px",background:C.redBg,color:C.red,borderRadius:8,fontSize:12.5,marginBottom:12,fontWeight:600}}>{err}</div>}

    <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:14}}>
      {stat(count(7),"Last 7 days")}
      {stat(count(30),"Last 30 days")}
      {stat(letters.length,"All time")}
      {stat(people,"People ("+(PERIODS.find(function(p){return p.k===period;})||{}).l+")")}
      {stat(responded,"Responded / became a file",C.green)}
    </div>

    <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:12}}>
      {PERIODS.map(function(p){const on=period===p.k;return <button key={p.k} onClick={function(){setPeriod(p.k);}} style={{padding:"7px 14px",borderRadius:20,cursor:"pointer",fontSize:12,fontWeight:800,fontFamily:"'Mulish',system-ui,sans-serif",border:"1px solid "+(on?C.accent:C.border),background:on?C.accent:C.card,color:on?"#fff":C.textMuted}}>{p.l}</button>;})}
      <Inp value={q} onChange={function(e){setQ(e.target.value);}} placeholder="Search name or address" style={{flex:"1 1 220px",padding:"8px 12px",fontSize:13}}/>
    </div>

    {leads===null?<div style={{color:C.textDim,fontSize:13}}>Loading letters…</div>:
    shown.length===0?<div style={{padding:"26px 20px",textAlign:"center",border:"1px dashed "+C.border,borderRadius:12,color:C.textDim,fontSize:13}}>No letters in this period. Log one from New Call with “✉ Confirm Handwritten Letter”.</div>:
    <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,overflow:"hidden"}}>
      {shown.map(function(x,i){const d=new Date(x.at);return(
        <div key={x.key} style={{display:"flex",alignItems:"center",gap:14,padding:"11px 16px",borderBottom:i<shown.length-1?"1px solid "+C.border:"none",flexWrap:"wrap"}}>
          <div style={{width:96,flexShrink:0}}>
            <div style={{fontSize:12.5,fontWeight:800,color:C.text}}>{d.toLocaleDateString()}</div>
            <div style={{fontSize:11,color:C.textDim}}>{d.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</div>
          </div>
          <div style={{flex:"1 1 260px",minWidth:0}}>
            <div style={{fontSize:13.5,fontWeight:800,color:C.text}}>{x.name}{x.of>1&&<span style={{fontSize:11,fontWeight:700,color:C.accentLight,marginLeft:8}}>letter {x.nth} of {x.of}</span>}</div>
            <div style={{fontSize:12,color:C.textMuted,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{x.address||"no address"}</div>
          </div>
          {x.outcome&&<span style={{fontSize:11,fontWeight:800,color:x.responded?C.green:C.textMuted,background:x.responded?C.greenBg:C.surfaceAlt,borderRadius:20,padding:"3px 10px"}}>{x.outcome}</span>}
          <Btn small disabled={!!opening} onClick={function(){work(x);}}>{Ic.phone} {opening&&x.lead&&opening===x.lead.id?"Loading…":"Open"}</Btn>
        </div>);})}
    </div>}
  </div>);
}

export { Letters };
